/*
 * Context usage 数据规范化层 (1:1 移植自 XHeyClaw-Web/lib/context-usage.ts)
 *
 * 后端在 /api/dtalk/sessions 列表里给每条 session 挂 context_usage 字段, 此模块负责
 * 把它解析成 SessionContextUsage 结构 + 补算 usedPercent / remainingTokens 等派生字段.
 */

export interface ContextUsagePart {
  id: string
  label: string
  tokens: number
  percentOfContext?: number
  percentOfUsed?: number
}

export interface ContextUsageEntry {
  name: string
  tokens: number
  truncated?: boolean
}

export interface CacheBreakdown {
  inputUncached: number
  cacheRead: number
  cacheWrite: number
  output?: number
  total?: number
  providerFamily?: string
}

export interface ToolInvocationEntry {
  toolCallId: string
  toolName: string
  tokens: number
  textTokens?: number
  imageTokens?: number
  sequenceIndex?: number
  preview?: string
  percentOfContext?: number
  percentOfUsed?: number
}

export interface MessageEntry {
  messageId: string
  role: string
  tokens: number
  preview: string
  hasImage?: boolean
  timestamp?: number
  toolCallId?: string
  toolName?: string
  percentOfContext?: number
  percentOfUsed?: number
}

export interface MultimodalImage {
  messageId?: string
  width: number
  height: number
  tokens: number
  formula?: string
}

export interface MultimodalBreakdown {
  totalTokens: number
  imageCount: number
  images: MultimodalImage[]
}

export interface ToolSchemaEntry {
  name: string
  tokens: number
  schemaTokens?: number
  summaryTokens?: number
}

export interface SessionContextUsage {
  totalTokens?: number
  contextTokens?: number
  usedPercent?: number
  remainingTokens?: number
  totalTokensFresh?: boolean
  estimated?: boolean
  parts: ContextUsagePart[]
  debugParts: ContextUsagePart[]
  systemPromptSections?: ContextUsagePart[]
  toolEntries?: ContextUsageEntry[]
  skillEntries?: ContextUsageEntry[]
  fileEntries?: ContextUsageEntry[]
  toolSchemaEntries?: ToolSchemaEntry[]
  cacheBreakdown?: CacheBreakdown
  toolInvocations?: ToolInvocationEntry[]
  messageEntries?: MessageEntry[]
  multimodal?: MultimodalBreakdown
  effectiveLimit?: number
  maxOutputTokens?: number
  warnings?: string[]
}

export const PART_COLORS: Record<string, string> = {
  system_prompt: 'aiClerk-ctxind__dot--system',
  tool_schemas: 'aiClerk-ctxind__dot--tools',
  conversation: 'aiClerk-ctxind__dot--conversation',
  project_context: 'aiClerk-ctxind__dot--project',
  workspace_files: 'aiClerk-ctxind__dot--workspace',
  skills: 'aiClerk-ctxind__dot--skills',
  tool_list: 'aiClerk-ctxind__dot--toollist'
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : null
}

function readNumber(
  record: Record<string, unknown>,
  snakeKey: string,
  camelKey: string
): number | undefined {
  const raw = record[snakeKey] ?? record[camelKey]
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 0) {
    return undefined
  }
  return raw
}

function readBoolean(
  record: Record<string, unknown>,
  snakeKey: string,
  camelKey: string
): boolean | undefined {
  const raw = record[snakeKey] ?? record[camelKey]
  return typeof raw === 'boolean' ? raw : undefined
}

function normalizePart(value: unknown): ContextUsagePart | null {
  const record = asRecord(value)
  if (!record) return null
  const id = typeof record.id === 'string' ? record.id : ''
  const label = typeof record.label === 'string' ? record.label : id
  const tokens = readNumber(record, 'tokens', 'tokens')
  if (!id || !label || tokens === undefined) return null
  return {
    id,
    label,
    tokens,
    percentOfContext: readNumber(
      record,
      'percent_of_context',
      'percentOfContext'
    ),
    percentOfUsed: readNumber(record, 'percent_of_used', 'percentOfUsed')
  }
}

function normalizeParts(value: unknown): ContextUsagePart[] {
  return Array.isArray(value)
    ? value
        .map(normalizePart)
        .filter((part): part is ContextUsagePart => !!part)
    : []
}

function normalizeEntry(value: unknown): ContextUsageEntry | null {
  const record = asRecord(value)
  if (!record) return null
  const name = typeof record.name === 'string' ? record.name : ''
  const tokens = readNumber(record, 'tokens', 'tokens')
  if (!name || tokens === undefined) return null
  return {
    name,
    tokens,
    truncated:
      typeof record.truncated === 'boolean' ? record.truncated : undefined
  }
}

function normalizeEntries(value: unknown): ContextUsageEntry[] | undefined {
  if (!Array.isArray(value)) return undefined
  const entries = value
    .map(normalizeEntry)
    .filter((e): e is ContextUsageEntry => !!e)
  return entries.length > 0 ? entries : undefined
}

function normalizeCacheBreakdown(value: unknown): CacheBreakdown | undefined {
  const record = asRecord(value)
  if (!record) return undefined
  const inputUncached = readNumber(record, 'input_uncached', 'inputUncached')
  const cacheRead = readNumber(record, 'cache_read', 'cacheRead')
  const cacheWrite = readNumber(record, 'cache_write', 'cacheWrite')
  if (
    inputUncached === undefined &&
    cacheRead === undefined &&
    cacheWrite === undefined
  ) {
    return undefined
  }
  return {
    inputUncached: inputUncached ?? 0,
    cacheRead: cacheRead ?? 0,
    cacheWrite: cacheWrite ?? 0,
    output: readNumber(record, 'output', 'output'),
    total: readNumber(record, 'total', 'total'),
    providerFamily:
      typeof record.provider_family === 'string'
        ? record.provider_family
        : typeof record.providerFamily === 'string'
          ? record.providerFamily
          : undefined
  }
}

function normalizeToolInvocations(
  value: unknown
): ToolInvocationEntry[] | undefined {
  if (!Array.isArray(value)) return undefined
  const out: ToolInvocationEntry[] = []
  for (const item of value) {
    const record = asRecord(item)
    if (!record) continue
    const toolCallId =
      typeof record.tool_call_id === 'string'
        ? record.tool_call_id
        : typeof record.toolCallId === 'string'
          ? record.toolCallId
          : ''
    const toolName =
      typeof record.tool_name === 'string'
        ? record.tool_name
        : typeof record.toolName === 'string'
          ? record.toolName
          : '(unknown)'
    const tokens = readNumber(record, 'tokens', 'tokens')
    if (tokens === undefined) continue
    out.push({
      toolCallId,
      toolName,
      tokens,
      textTokens: readNumber(record, 'text_tokens', 'textTokens'),
      imageTokens: readNumber(record, 'image_tokens', 'imageTokens'),
      sequenceIndex: readNumber(record, 'sequence_index', 'sequenceIndex'),
      preview: typeof record.preview === 'string' ? record.preview : undefined,
      percentOfContext: readNumber(
        record,
        'percent_of_context',
        'percentOfContext'
      ),
      percentOfUsed: readNumber(record, 'percent_of_used', 'percentOfUsed')
    })
  }
  return out.length > 0 ? out : undefined
}

function normalizeMessageEntries(value: unknown): MessageEntry[] | undefined {
  if (!Array.isArray(value)) return undefined
  const out: MessageEntry[] = []
  for (const item of value) {
    const record = asRecord(item)
    if (!record) continue
    const tokens = readNumber(record, 'tokens', 'tokens')
    if (tokens === undefined) continue
    out.push({
      messageId: typeof record.message_id === 'string' ? record.message_id : '',
      role: typeof record.role === 'string' ? record.role : '',
      tokens,
      preview: typeof record.preview === 'string' ? record.preview : '',
      hasImage: readBoolean(record, 'has_image', 'hasImage'),
      timestamp: readNumber(record, 'timestamp', 'timestamp'),
      toolCallId:
        typeof record.tool_call_id === 'string'
          ? record.tool_call_id
          : undefined,
      toolName:
        typeof record.tool_name === 'string' ? record.tool_name : undefined,
      percentOfContext: readNumber(
        record,
        'percent_of_context',
        'percentOfContext'
      ),
      percentOfUsed: readNumber(record, 'percent_of_used', 'percentOfUsed')
    })
  }
  return out.length > 0 ? out : undefined
}

function normalizeMultimodal(value: unknown): MultimodalBreakdown | undefined {
  const record = asRecord(value)
  if (!record) return undefined
  const totalTokens = readNumber(record, 'total_tokens', 'totalTokens')
  const imageCount = readNumber(record, 'image_count', 'imageCount')
  if (totalTokens === undefined && imageCount === undefined) return undefined
  const imagesRaw = record.images
  const images: MultimodalImage[] = []
  if (Array.isArray(imagesRaw)) {
    for (const it of imagesRaw) {
      const r = asRecord(it)
      if (!r) continue
      const w = readNumber(r, 'width', 'width') ?? 0
      const h = readNumber(r, 'height', 'height') ?? 0
      const t = readNumber(r, 'tokens', 'tokens') ?? 0
      images.push({
        messageId:
          typeof r.message_id === 'string'
            ? r.message_id
            : typeof r.messageId === 'string'
              ? r.messageId
              : undefined,
        width: w,
        height: h,
        tokens: t,
        ...(typeof r.formula === 'string' && r.formula
          ? { formula: r.formula }
          : {})
      })
    }
  }
  return {
    totalTokens: totalTokens ?? 0,
    imageCount: imageCount ?? images.length,
    images
  }
}

function normalizeToolSchemaEntries(
  value: unknown
): ToolSchemaEntry[] | undefined {
  if (!Array.isArray(value)) return undefined
  const out: ToolSchemaEntry[] = []
  for (const item of value) {
    const record = asRecord(item)
    if (!record) continue
    const name = typeof record.name === 'string' ? record.name : ''
    const tokens = readNumber(record, 'tokens', 'tokens')
    if (!name || tokens === undefined) continue
    out.push({
      name,
      tokens,
      schemaTokens: readNumber(record, 'schema_tokens', 'schemaTokens'),
      summaryTokens: readNumber(record, 'summary_tokens', 'summaryTokens')
    })
  }
  return out.length > 0 ? out : undefined
}

export function normalizeContextUsage(
  value: unknown
): SessionContextUsage | null {
  const record = asRecord(value)
  if (!record) return null
  const usage: SessionContextUsage = {
    totalTokens: readNumber(record, 'total_tokens', 'totalTokens'),
    contextTokens: readNumber(record, 'context_tokens', 'contextTokens'),
    usedPercent: readNumber(record, 'used_percent', 'usedPercent'),
    remainingTokens: readNumber(record, 'remaining_tokens', 'remainingTokens'),
    totalTokensFresh: readBoolean(
      record,
      'total_tokens_fresh',
      'totalTokensFresh'
    ),
    estimated: readBoolean(record, 'estimated', 'estimated'),
    parts: normalizeParts(record.parts),
    debugParts: normalizeParts(record.debug_parts ?? record.debugParts),
    systemPromptSections: normalizeParts(
      record.system_prompt_sections ?? record.systemPromptSections
    ),
    toolEntries: normalizeEntries(record.tool_entries ?? record.toolEntries),
    skillEntries: normalizeEntries(record.skill_entries ?? record.skillEntries),
    fileEntries: normalizeEntries(record.file_entries ?? record.fileEntries),
    toolSchemaEntries: normalizeToolSchemaEntries(
      record.tool_schema_entries ?? record.toolSchemaEntries
    ),
    cacheBreakdown: normalizeCacheBreakdown(
      record.cache_breakdown ?? record.cacheBreakdown
    ),
    toolInvocations: normalizeToolInvocations(
      record.tool_invocations ?? record.toolInvocations
    ),
    messageEntries: normalizeMessageEntries(
      record.message_entries ?? record.messageEntries
    ),
    multimodal: normalizeMultimodal(record.multimodal),
    effectiveLimit: readNumber(record, 'effective_limit', 'effectiveLimit'),
    maxOutputTokens: readNumber(record, 'max_output_tokens', 'maxOutputTokens'),
    warnings: Array.isArray(record.warnings)
      ? record.warnings.filter((w): w is string => typeof w === 'string')
      : undefined
  }

  // 后端目前 /sessions/{id}/context-usage 不回顶层 total_tokens, 只回 parts 明细;
  // 用 parts.tokens 之和当 totalTokens, 让圆环能填色 (跟 ContextUsageDetail 弹层数据一致).
  if (
    usage.totalTokens === undefined &&
    Array.isArray(usage.parts) &&
    usage.parts.length > 0
  ) {
    const partsSum = usage.parts.reduce(
      (acc: number, p: ContextUsagePart) =>
        acc + (typeof p.tokens === 'number' ? p.tokens : 0),
      0
    )
    if (partsSum > 0) usage.totalTokens = partsSum
  }

  if (
    usage.usedPercent === undefined &&
    usage.totalTokens !== undefined &&
    usage.contextTokens !== undefined &&
    usage.contextTokens > 0
  ) {
    usage.usedPercent = (usage.totalTokens / usage.contextTokens) * 100
  }

  if (
    usage.remainingTokens === undefined &&
    usage.totalTokens !== undefined &&
    usage.contextTokens !== undefined
  ) {
    usage.remainingTokens = Math.max(0, usage.contextTokens - usage.totalTokens)
  }

  if (
    usage.totalTokens === undefined &&
    usage.contextTokens === undefined &&
    usage.usedPercent === undefined &&
    usage.parts.length === 0 &&
    usage.debugParts.length === 0
  ) {
    return null
  }
  return usage
}

export function getSessionContextUsage(
  session: unknown
): SessionContextUsage | null {
  const record = asRecord(session)
  if (!record) return null
  return normalizeContextUsage(record.context_usage ?? record.contextUsage)
}

export function formatTokens(tokens?: number): string {
  if (tokens === undefined) return '--'
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}K`
  return String(Math.round(tokens))
}

export function formatPercent(percent?: number): string {
  if (percent === undefined) return '--'
  return `${Math.round(percent)}%`
}

export function getContextUsageTone(
  usage: SessionContextUsage
): 'ok' | 'warn' | 'danger' | 'unknown' {
  const percent = usage.usedPercent
  if (percent === undefined) return 'unknown'
  if (percent >= 90) return 'danger'
  if (percent >= 75) return 'warn'
  return 'ok'
}

/** parts 不存在时用 debugParts 求和作为已用 tokens 兜底 */
export function estimateUsedTokens(
  usage: SessionContextUsage
): number | undefined {
  if (usage.totalTokens !== undefined) return usage.totalTokens
  const source = usage.parts.length > 0 ? usage.parts : usage.debugParts
  if (source.length === 0) return undefined
  return source.reduce((sum, p) => sum + p.tokens, 0)
}
